import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Main, Text } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { GENERAL_CONSTANTS } from 'constants/general';

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Space.spBase};
  margin-right: ${Space.sp3x};
`;

const ToggleLabel = styled.div`
  color: ${Text.primary};
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`;

const ToggleSwitch = styled.div<{ isActive: boolean; disabled?: boolean }>`
  position: relative;
  width: 48px;
  height: 24px;
  background-color: ${props => props.disabled 
    ? '#E4E7EC' 
    : props.isActive ? Main.brandDark : '#E4E7EC'
  };
  border-radius: 12px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.3s ease;
  opacity: ${props => props.disabled ? 0.5 : 1};
  outline: none;
  
  &::before {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: white;
    top: 3px;
    left: ${props => props.isActive ? '27px' : '3px'};
    transition: left 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

interface AdminViewToggleProps {
  isAdminView: boolean;
  onToggle: (isAdminView: boolean) => void;
  className?: string;
  disabled?: boolean;
}

const AdminViewToggle: React.FC<AdminViewToggleProps> = React.memo(({
  isAdminView,
  onToggle,
  className,
  disabled = false
}) => {
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const DEBOUNCE_DELAY = 300; // 300ms debounce delay

  const handleToggle = useCallback(() => {
    if (disabled) return;

    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set a new timeout for debouncing
    debounceTimeoutRef.current = setTimeout(() => {
      onToggle(!isAdminView);
      debounceTimeoutRef.current = null;
    }, DEBOUNCE_DELAY);
  }, [isAdminView, onToggle, disabled]);

  // Clean up timeout on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  }, [handleToggle]);

  return (
    <ToggleContainer className={className}>
      <ToggleSwitch 
        isActive={isAdminView}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="switch"
        aria-checked={isAdminView}
        aria-label="Toggle admin view"
        aria-disabled={disabled}
      />
      <ToggleLabel>{GENERAL_CONSTANTS.ADMIN_VIEW}</ToggleLabel>
    </ToggleContainer>
  );
});

AdminViewToggle.displayName = 'AdminViewToggle';

export default AdminViewToggle;